"use client";

import { useState, useEffect, useCallback } from "react";
import {
  ThemeColors,
  FontFamilies,
  DEFAULT_THEME,
  DEFAULT_FONT,
  type ThemeColorKey,
  type FontFamilyKey,
} from "@/lib/constants";

const THEME_KEY = "zoey_theme_color";
const FONT_KEY = "zoey_font_family";

export function useTheme() {
  const [themeColor, setThemeColorState] = useState<ThemeColorKey>(DEFAULT_THEME);
  const [fontFamily, setFontFamilyState] = useState<FontFamilyKey>(DEFAULT_FONT);

  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_KEY) as ThemeColorKey | null;
    const savedFont = localStorage.getItem(FONT_KEY) as FontFamilyKey | null;
    if (savedTheme && savedTheme in ThemeColors) setThemeColorState(savedTheme);
    if (savedFont && savedFont in FontFamilies) setFontFamilyState(savedFont);
  }, []);

  // Apply theme to CSS custom properties
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--accent-color", ThemeColors[themeColor].value);
    root.style.setProperty("--font-family", FontFamilies[fontFamily].value);
  }, [themeColor, fontFamily]);

  const setThemeColor = useCallback((color: ThemeColorKey) => {
    setThemeColorState(color);
    localStorage.setItem(THEME_KEY, color);
  }, []);

  const setFontFamily = useCallback((font: FontFamilyKey) => {
    setFontFamilyState(font);
    localStorage.setItem(FONT_KEY, font);
  }, []);

  return {
    themeColor,
    fontFamily,
    accentColor: ThemeColors[themeColor].value,
    fontFamilyValue: FontFamilies[fontFamily].value,
    setThemeColor,
    setFontFamily,
  };
}
